import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { AuthContext } from '@/App';
import { Sparkles, Check, Crown, Zap, TrendingUp, Upload, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [country, setCountry] = useState('IN');
  const [price, setPrice] = useState('₹799');
  const [uploadingProof, setUploadingProof] = useState(false);

  useEffect(() => {
    // Detect user location
    fetch('https://ipapi.co/json/')
      .then(res => res.json())
      .then(data => {
        const userCountry = data.country_code;
        setCountry(userCountry);
        if (userCountry === 'IN') {
          setPrice('₹799');
        } else {
          setPrice('$10');
        }
      })
      .catch(() => {
        // Default to India
        setPrice('₹799');
      });

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/payment-button.js';
    script.setAttribute('data-payment_button_id', 'pl_SS7xxgRqoPOeaw');
    script.async = true;
    
    const razorpayContainer = document.getElementById('razorpay-button-container');
    if (razorpayContainer && showPaymentOptions) {
      razorpayContainer.innerHTML = '';
      razorpayContainer.appendChild(script);
    }
  }, [showPaymentOptions]);

  const handleUploadPaymentProof = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingProof(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.id);
    formData.append('email', user.email);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/payment/upload-proof`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Payment proof uploaded! We will verify and activate your premium account within 24 hours.');
    } catch (error) {
      toast.error('Failed to upload payment proof');
    } finally {
      setUploadingProof(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 h-20 flex items-center justify-between px-8">
        <div className="flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <span className="text-2xl font-bold gradient-text">DashAI</span>
        </div>
        <Link to="/dashboard">
          <Button variant="ghost" data-testid="back-to-dashboard-btn">Back to Dashboard</Button>
        </Link>
      </header>

      <div className="container mx-auto px-8 py-24 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight mb-6">
            <span className="gradient-text">Upgrade to Premium</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unlock unlimited AI-powered dashboards and take your data analysis to the next level
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Free Plan */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-zinc-950/50 backdrop-blur-md border border-white/10 rounded-2xl p-8"
          >
            <div className="mb-6">
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">$0</span>
                <span className="text-muted-foreground">/month</span>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                '3 dashboard generations',
                'AI-powered analysis',
                'Basic visualizations',
                'Excel export',
                'Share via link'
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              className="w-full rounded-full py-6"
              disabled
              data-testid="free-plan-btn"
            >
              Current Plan
            </Button>
          </motion.div>

          {/* Premium Plan */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-primary/20 to-accent/20 border-2 border-primary rounded-2xl p-8 relative overflow-hidden"
          >
            <div className="absolute top-4 right-4">
              <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                POPULAR
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-6 w-6 text-primary" />
                <h3 className="text-2xl font-bold">Premium</h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">{price}</span>
                <span className="text-muted-foreground">/{country === 'IN' ? 'one-time' : 'one-time'}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {country === 'IN' ? '🇮🇳 India Price' : '🌍 International Price'}
              </p>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                'Unlimited dashboards',
                'Advanced AI analysis',
                'Premium visualizations',
                'Priority support',
                'Download: Excel, PDF, Word, PPT',
                'Share via link',
                'Custom branding',
                'API access'
              ].map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {user?.is_premium ? (
              <Button
                className="w-full rounded-full py-6 font-semibold"
                disabled
                data-testid="premium-active-btn"
              >
                <Crown className="h-5 w-5 mr-2" /> Active
              </Button>
            ) : (
              <>
                {!showPaymentOptions ? (
                  <Button
                    onClick={() => setShowPaymentOptions(true)}
                    className="w-full rounded-full py-6 text-lg font-semibold shadow-lg shadow-primary/25 hover:scale-105 transition-transform"
                    data-testid="show-payment-options-btn"
                  >
                    <Zap className="h-5 w-5 mr-2" /> Choose Payment Method
                  </Button>
                ) : (
                  <div className="space-y-4 mt-4">
                    <h4 className="text-lg font-semibold text-center mb-4">Select Payment Option:</h4>
                    
                    {/* Razorpay Payment Button */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center">
                      <h5 className="text-md font-semibold mb-4 flex items-center justify-center gap-2">
                        <span className="text-2xl">💳</span> Pay with Card/UPI/Wallet
                      </h5>
                      <div id="razorpay-button-container" className="flex justify-center"></div>
                      <p className="text-xs text-muted-foreground mt-3">Secure payment via Razorpay</p>
                    </div>

                    {/* GPay QR Code Option */}
                    {country === 'IN' && (
                      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                        <h5 className="text-md font-semibold mb-4 text-center flex items-center justify-center gap-2">
                          <QrCode className="h-5 w-5" /> Pay via GPay/PhonePe
                        </h5>
                        <div className="flex flex-col items-center">
                          <img 
                            src="https://customer-assets.emergentagent.com/job_ai-dash-builder-2/artifacts/7vafgrxa_WhatsApp%20Image%202026-03-17%20at%2012.30.46%20AM.jpeg" 
                            alt="GPay QR Code" 
                            className="w-48 h-48 rounded-lg border-2 border-primary/30 mb-4"
                          />
                          <p className="text-sm text-center mb-4">
                            Scan QR code and pay ₹799, then upload screenshot below
                          </p>
                          <div className="w-full">
                            <input
                              type="file"
                              id="payment-proof"
                              accept="image/*"
                              onChange={handleUploadPaymentProof}
                              className="hidden"
                              disabled={uploadingProof}
                            />
                            <label htmlFor="payment-proof">
                              <Button
                                type="button"
                                variant="outline"
                                className="w-full rounded-full"
                                disabled={uploadingProof}
                                onClick={() => document.getElementById('payment-proof').click()}
                                data-testid="upload-payment-proof-btn"
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {uploadingProof ? 'Uploading...' : 'Upload Payment Screenshot'}
                              </Button>
                            </label>
                          </div>
                          <p className="text-xs text-muted-foreground mt-3 text-center">
                            Premium activation within 24 hours after verification
                          </p>
                        </div>
                      </div>
                    )}

                    <Button
                      variant="ghost"
                      onClick={() => setShowPaymentOptions(false)}
                      className="w-full"
                      data-testid="back-to-plans-btn"
                    >
                      ← Back to Plans
                    </Button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>

        {/* Features Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-24 max-w-4xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-center mb-12">Why Upgrade?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'Unlimited Power',
                description: 'Generate as many dashboards as you need without any restrictions'
              },
              {
                icon: TrendingUp,
                title: 'Advanced Analytics',
                description: 'Get deeper insights with premium AI algorithms and visualizations'
              },
              {
                icon: Crown,
                title: 'Priority Support',
                description: 'Get help when you need it with dedicated premium support'
              }
            ].map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-4">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;